import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, Check, Eye, EyeOff, RefreshCw, Info } from 'lucide-react'
import { PageSkeleton } from '@/components/shared/page-skeleton'
import { useSettings, QUERY_KEYS } from '@/hooks'
import { api, type MerchantSettings } from '@/lib/api'
import { SectionCard } from '@/components/shared/section-card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { WebhookEndpointList } from '@/components/webhooks/WebhookEndpointList'

export const Route = createFileRoute('/_app/settings/developers')({
  component: DeveloperSettingsPage,
})

function DeveloperSettingsPage() {
  const { data: settings, isLoading } = useSettings()

  if (isLoading) {
    return <PageSkeleton sections={2} />
  }

  if (!settings) return null

  return (
    <div className="space-y-8">
      <PageHeader
        size="sm"
        title="Desenvolvedor"
        description="Acesse a API e configure notificações automáticas."
      />

      <ApiKeysSection settings={settings} />

      <Separator />

      <SectionCard
        title="Notificações"
        description="Receba eventos em tempo real no seu sistema quando algo acontecer no Rafobase."
      >
        <WebhookEndpointList />
      </SectionCard>
    </div>
  )
}

function ApiKeysSection({ settings }: { settings: MerchantSettings }) {
  const queryClient = useQueryClient()
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [showApiKey, setShowApiKey] = useState(false)

  const regenerateApiKey = useMutation({
    mutationFn: () => api.settings.regenerateApiKey(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings })
    },
  })

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <SectionCard
      title="Chaves de API"
      description="Credenciais para acesso à API (Server-to-Server)."
    >
      <div className="space-y-4">
        {settings.publicApiKey && (
          <div className="rounded-md bg-blue-50 p-4 border border-blue-200 dark:bg-blue-900/10 dark:border-blue-900/50">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-500 shrink-0" />
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-400">
                  Chave de API Pública
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-500/90">
                  Esta chave é utilizada para autenticar as requisições da sua integração. Ela pode
                  ser utilizada com segurança no seu frontend (código client-side).
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>Chave de API Pública</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                value={settings.publicApiKey || 'Nenhuma chave gerada'}
                readOnly
                type={showApiKey ? 'text' : 'password'}
                className="font-mono text-sm pr-10"
              />
              {settings.publicApiKey && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:bg-transparent"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              )}
            </div>

            <ChonkyButton
              variant="secondary"
              size="icon"
              onClick={() =>
                settings.publicApiKey && copyToClipboard(settings.publicApiKey, 'api-key')
              }
              disabled={!settings.publicApiKey}
              title="Copiar"
            >
              {copiedId === 'api-key' ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </ChonkyButton>
            <ChonkyButton
              variant={settings.publicApiKey ? 'secondary' : 'primary'}
              onClick={() => {
                if (
                  !settings.publicApiKey ||
                  window.confirm('Tem certeza? A chave antiga deixará de funcionar imediatamente.')
                ) {
                  regenerateApiKey.mutate()
                }
              }}
              loading={regenerateApiKey.isPending}
              title="Redefinir Chave"
            >
              {settings.publicApiKey ? <RefreshCw className="h-4 w-4" /> : 'Gerar Chave'}
            </ChonkyButton>
          </div>
          <p className="text-xs text-muted-foreground">
            Utilize esta chave no header <code>Authorization: Bearer &lt;CHAVE&gt;</code>.
          </p>
        </div>
      </div>
    </SectionCard>
  )
}
